import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import Button from "react-bootstrap/Button";
import { employeeHandler } from "../../../redux/handlers";
import { useGlobalTable } from "../../../hooks/useGlobalTable";
import { createTableColumns } from "../../../utils/createTableColumns";
import { DepartmentModal } from "./modals/DepartmentModal";
import { SuccessDeleteModal } from "./modals/SuccessDeleteModal";
import { AllderTable } from "../../../components/AllderTable";
import { useBreadcrumb } from "../../../hooks/useBreadcrumb";
import Breadcrumb from "../../../components/Breadcrumb";

const INITIAL_SELECTED_DEPARTMENT = {
  id: "",
  code: "",
  name_th: "",
  name_en: "",
};

const INITIAL_MODAL_STATE = {
  open: false,
  x: 0,
  y: 0,
  width: 470,
  pin: false,
  mode: "normal",
};

const MODAL_HEIGHTS = {
  FORM: 510,
  CONFIRM: 320,
  SUCCESS: 300,
};

const MODAL_STATES = {
  CREATE: { ...INITIAL_MODAL_STATE, height: MODAL_HEIGHTS.FORM },
  EDIT: { ...INITIAL_MODAL_STATE, height: MODAL_HEIGHTS.FORM },
  CONFIRM: { ...INITIAL_MODAL_STATE, height: MODAL_HEIGHTS.CONFIRM },
  SUCCESS: { ...INITIAL_MODAL_STATE, height: MODAL_HEIGHTS.SUCCESS },
};

export function DepartmentManagement() {
  const dispatch = useDispatch();
  const { businessSlug } = useParams();
  const [selectedDepartment, setSelectedDepartment] = useState(
    INITIAL_SELECTED_DEPARTMENT
  );
  const [successMsg, setSuccessMsg] = useState("");
  const [modalCreate, setModalCreate] = useState(MODAL_STATES.CREATE);
  const [modalEdit, setModalEdit] = useState(MODAL_STATES.EDIT);
  const [modalSuccess, setModalSuccess] = useState(MODAL_STATES.SUCCESS);

  const fetchDepartments = useCallback(
    (params) =>
      dispatch(
        employeeHandler.getEmployeeDepartments({
          business_slug: businessSlug,
          ...params,
        })
      ),
    [businessSlug, dispatch]
  );

  const {
    data: departments,
    loading,
    totalRows,
    paginationModel,
    setPaginationModel,
    handleSort,
    debouncedSearch,
    refreshData: refreshDepartments,
    filterParams,
  } = useGlobalTable({
    fetchData: fetchDepartments,
    defaultSort: "created_at",
    defaultOrder: "desc",
    defaultPageSize: 20,
  });
  const breadcrumbConfig = [
    { label: "หน้าหลัก", path: "/" },
    { label: "พนักงาน", path: "#" },
    { label: "แผนกงาน" },
  ];
  useBreadcrumb(breadcrumbConfig);

  const handleCreateDepartment = async (values) => {
    try {
      await dispatch(
        employeeHandler.createEmployeeDepartment({
          businessSlug,
          code: values.code,
          name_th: values.name_th,
          name_en: values.name_en,
        })
      );
      refreshDepartments();
      setSuccessMsg("บันทึกแผนกสำเร็จ");
      setModalCreate((prev) => ({ ...prev, open: false }));
      setModalSuccess((prev) => ({ ...prev, open: true }));
      setSelectedDepartment(INITIAL_SELECTED_DEPARTMENT);
    } catch (error) {
      console.error("Error creating department:", error);
    }
  };

  const handleUpdateDepartment = async (values) => {
    try {
      await dispatch(
        employeeHandler.updateEmployeeDepartment(values.id, {
          businessSlug,
          code: values.code,
          name_th: values.name_th,
          name_en: values.name_en,
        })
      );
      refreshDepartments();
      setSuccessMsg("แก้ไขแผนกสำเร็จ");
      setModalEdit((prev) => ({ ...prev, open: false }));
      setModalSuccess((prev) => ({ ...prev, open: true }));
    } catch (error) {
      console.error("Error updating department:", error);
    }
  };

  const handleExport = async () => {
    try {
      await dispatch(
        employeeHandler.exportEmployeeDepartmentToExcel(
          businessSlug,
          filterParams.s
        )
      );
    } catch (error) {
      console.error("Error exporting data:", error);
    }
  };

  const columns = createTableColumns(
    [
      {
        field: "code",
        headerName: "รหัสแผนก",
        width: 170,
        sortable: true,
        renderCell: ({ row }) => (
          <div
            style={{
              color: "#2DC76D",
              fontWeight: 500,
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => {
              setSelectedDepartment(row);
              setModalEdit((prev) => ({ ...prev, open: true }));
            }}
          >
            {row.code}
          </div>
        ),
      },
      {
        field: "name_th",
        headerName: "ชื่อแผนก",
        width: 340,
        sortable: true,
        flex: 1,
        renderCell: ({ row }) => (
          <div style={{ fontWeight: 500 }}>{row.name_th}</div>
        ),
      },
      {
        field: "emp_count",
        headerName: "จำนวนพนักงาน",
        width: 180,
        sortable: false,
      },
      {
        field: "actions",
        headerName: "จัดการ",
        width: 120,
        sortable: false,
        type: "actions",
      },
    ],
    (row) => (
      <div>
        <Button
          type="button"
          className="btn-edit-st-1"
          onClick={() => {
            setSelectedDepartment(row);
            setModalEdit((prev) => ({ ...prev, open: true }));
          }}
        />
      </div>
    )
  );

  return (
    <div>
      <Breadcrumb />
      <div className="card card-st-2">
        <div className="card-body px-0 py-4">
          <div className="text-lg font-bold mb-4 lh-1 px-4 mb-4 text-[#2DC76D]">
            แผนกงาน
          </div>
          <div className="d-flex align-items-center px-4 mb-4">
            <button
              className="btn btn-export-st-1 me-auto"
              onClick={handleExport}
            >
              Export
            </button>
            <div className="me-3">
              <input
                type="text"
                className="form-control form-st-4"
                placeholder="ค้นหาชื่อแผนกงาน"
                onChange={(e) => debouncedSearch(e.target.value)}
              />
            </div>
            <Button
              type="button"
              className="btn btn-st-7"
              onClick={() => {
                setModalCreate((prev) => ({ ...prev, open: true }));
              }}
            >
              เพิ่มแผนก
            </Button>
          </div>

          <AllderTable
            data={departments}
            loading={loading}
            columns={columns}
            totalRows={totalRows}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            handleSort={handleSort}
          />
        </div>
      </div>

      <DepartmentModal
        modal={modalCreate}
        setModal={setModalCreate}
        onHide={() => setModalCreate((prev) => ({ ...prev, open: false }))}
        onSubmit={handleCreateDepartment}
      />

      <DepartmentModal
        modal={modalEdit}
        setModal={setModalEdit}
        isEdit={true}
        departmentData={selectedDepartment}
        onHide={() => setModalEdit((prev) => ({ ...prev, open: false }))}
        onSubmit={handleUpdateDepartment}
      />

      <SuccessDeleteModal
        modal={modalSuccess}
        setModal={setModalSuccess}
        msg={successMsg}
        onHide={() => setModalSuccess((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
}
